import React, { useMemo, useState } from 'react';
import theme from './OptionGroupAccordion.module.scss';
import { reorderOptionItems } from '@/services/options/option-items';
import { LinkingType, OptionGroupDto, OptionGroupLinkStatus, OptionItemDto } from '@/types';
import { OptionCard } from '../../products/ProductCard';
import { ProductCardList } from '../../products/ProductCard/ProductCardList';
import { Button, Card } from '@ygtechnologies/rubus-ui';
import { Trans } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import { arrayMove, LoadingIndicator, Sortable, useAsyncState } from '@ygtechnologies/bygr-frontend-component-library';
import { ReorderPopupBar } from '@/components/ReorderPopupBar/ReorderPopupBar';
import { faCheck, faLink, faPencil, faPlus, faQuestion, faTimes } from '@fortawesome/pro-light-svg-icons';
import { OptionGroupStatusChangeDialog } from '../OptionGroupStatusChangeDialog';
import { useOptionItemsByGroup } from '@/services/options/option-items.hooks';
import { OptionGroupEditDialog } from '../OptionGroupEditDialog/OptionGroupEditDialog';
import { getProjectFeeSets } from '@/services/fees';

export interface OptionGroupAccordionDetailsProps {
    optionGroup: OptionGroupDto;
    readonly?: boolean;
}

// function getLinks(optionGroup: OptionGroupDto): (UnitLinkDto | RoomLinkDto)[] {
//     if (optionGroup.linkType === LinkingType.Direct) {
//         return optionGroup.linkedUnits;
//     } else {
//         return optionGroup.linkedRooms;
//     }
// }

export const OptionGroupAccordionDetails: React.FC<OptionGroupAccordionDetailsProps> = ({ optionGroup, readonly }) => {
    const { data: optionItems, refetch } = useOptionItemsByGroup(optionGroup.id);
    const [reorderedOptionItems, setReorderedOptionItems] = useState<OptionItemDto[] | null>(null);
    const [feeSets] = useAsyncState(
        () => (optionGroup.projectId ? getProjectFeeSets(optionGroup.projectId) : Promise.resolve([])),
        [optionGroup.projectId],
        null
    );
    const projectFeeSet = useMemo(
        () => feeSets?.find((x) => x.id === optionGroup.projectFeeSetId) ?? null,
        [feeSets, optionGroup.projectFeeSetId]
    );
    const queryClient = useQueryClient();

    const handleOptionChange = () => {
        refetch();
        queryClient.refetchQueries({ queryKey: ['optionGroups'] });
    };

    const cancelReorder = () => {
        setReorderedOptionItems(null);
    };

    const acceptReorder = async () => {
        if (!reorderedOptionItems) {
            return;
        }

        await reorderOptionItems(
            optionGroup.id,
            reorderedOptionItems.map((x) => x.id)
        );

        setReorderedOptionItems(null);
        refetch();
    };

    const onSortEnd = ({ oldIndex, newIndex }) => {
        const newArray = arrayMove(reorderedOptionItems ?? optionItems!, oldIndex, newIndex);
        setReorderedOptionItems(newArray);
    };

    if (!optionItems) {
        return <LoadingIndicator />;
    }

    const isOptionGroupPublished =
        optionGroup.linkType === LinkingType.Direct
            ? optionGroup.linkedUnits.some((x) => x.status === OptionGroupLinkStatus.Approved)
            : optionGroup.linkedRooms.some((x) => x.status === OptionGroupLinkStatus.Approved);

    return (
        <div className={theme.details}>
            <Sortable
                items={reorderedOptionItems ?? optionItems}
                idField={(x) => x.id}
                onSortEnd={onSortEnd}
                direction="both"
                container={ProductCardList}
                disabled={true}>
                {(item, { handleProps, isDragging }) => (
                    <OptionCard
                        key={item.id}
                        className={theme.sortableCard}
                        option={item}
                        onChange={handleOptionChange}
                        // dragProps={handleProps}
                        readOnly={readonly}
                        projectFeeSet={projectFeeSet}
                        isOptionGroupPublished={isOptionGroupPublished}
                    />
                )}
            </Sortable>
            <ReorderPopupBar open={!!reorderedOptionItems} onAccept={acceptReorder} onCancel={cancelReorder} />

            {!readonly && (
                <Card className={theme.footer}>
                    <div className={theme.buttons}>
                        {/* <OptionGroupAddProductDialog projectId={optionGroup.projectId} /> */}
                        <OptionGroupActionsBar optionGroup={optionGroup} />
                    </div>
                </Card>
            )}
        </div>
    );
};

interface OptionGroupActionsBarProps {
    optionGroup: OptionGroupDto;
}

const OptionGroupActionsBar: React.FC<OptionGroupActionsBarProps> = ({ optionGroup }) => {
    const [changeStatusDialog, setChangeStatusDialog] = useState<OptionGroupLinkStatus | null>(null);
    const [editOptionsDialogOpen, setEditOptionsDialogOpen] = useState(false);

    // const permissions = useMemo(() => {
    //     const links = getLinks(optionGroup);

    //     return {
    //         canApprove: links.some((x) => x.status !== OptionGroupLinkStatus.Approved),
    //         canReject: links.some((x) => x.status !== OptionGroupLinkStatus.Rejected),
    //         canUnpublish: links.some((x) => x.status !== OptionGroupLinkStatus.Pending),
    //     };
    // }, [optionGroup]);

    const queryClient = useQueryClient();

    return (
        <>
            <OptionGroupStatusChangeDialog
                optionGroup={optionGroup}
                status={changeStatusDialog}
                onClose={() => setChangeStatusDialog(null)}
                onAccept={() => queryClient.refetchQueries({ queryKey: ['optionGroups'] })}
            />

            <OptionGroupEditDialog
                open={editOptionsDialogOpen}
                optionGroup={optionGroup}
                toggle={setEditOptionsDialogOpen}
            />

            <Button
                onClick={() => setEditOptionsDialogOpen(true)}
                variant="tertiary"
                icon={faPencil}
                disabled={!optionGroup.permissions.canEdit}>
                <Trans>Edit</Trans>
            </Button>

            <Button
                variant="tertiary"
                icon={faQuestion}
                onClick={(e) => setChangeStatusDialog(OptionGroupLinkStatus.Pending)}
                disabled={!optionGroup.permissions.canUnpublish}>
                <Trans>Unpublish</Trans>
            </Button>
            <Button
                variant="tertiary"
                icon={faTimes}
                onClick={(e) => setChangeStatusDialog(OptionGroupLinkStatus.Rejected)}
                disabled={!optionGroup.permissions.canReject}>
                <Trans>Reject</Trans>
            </Button>
            <Button
                variant="tertiary"
                icon={faCheck}
                onClick={(e) => setChangeStatusDialog(OptionGroupLinkStatus.Approved)}
                disabled={!optionGroup.permissions.canApprove}>
                <Trans>Publish</Trans>
            </Button>
        </>
    );
};
